'use client'

import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { noop } from 'lodash'

import { DynamicAttribute } from '../types'
import { LayeredDynamicAttributesContextType } from '../types/context'
import useFieldErrors from '../hooks/useFieldErrors'
import { ItemAttributeModel } from '../types/item-edit'
import { reduceAttributes } from '../utils/dynamic-attribute'
import { NormalizedAttributes } from '../types/dynamic-attribute'

export const LayeredDynamicAttributesContext = createContext<LayeredDynamicAttributesContextType>({
  selectedLayeredDynamicAttributes: [],
  getLayeredDynamicAttributeValuesByCode: () => undefined,
  setLayeredDynamicAttributeValue: noop,
  setLayeredDynamicAttributeValues: noop,
})

export const LayeredDynamicAttributesContextProvider = ({ children }: { children: ReactNode }) => {
  const [layeredDynamicAttributes, setLayeredDynamicAttributes] = useState<
    NormalizedAttributes<ItemAttributeModel, 'ids'>
  >({
    byName: {},
    names: [],
  })

  const { removeFieldError } = useFieldErrors()

  const selectedLayeredDynamicAttributes = useMemo(
    () =>
      layeredDynamicAttributes.names.reduce<Array<DynamicAttribute>>((acc, name) => {
        const value = layeredDynamicAttributes.byName[name]
        if (value) {
          acc.push({
            field: name,
            value,
          })
        }

        return acc
      }, []),
    [layeredDynamicAttributes.byName, layeredDynamicAttributes.names],
  )

  const getLayeredDynamicAttributeValuesByCode = useCallback(
    (fieldName: string) => layeredDynamicAttributes.byName[fieldName] || undefined,
    [layeredDynamicAttributes.byName],
  )

  const setLayeredDynamicAttributeValue = useCallback(
    (code: string, value: Array<number>) => {
      setLayeredDynamicAttributes(prev => {
        const updatedByName = { ...prev.byName, [code]: value }
        const updatedNames = prev.names.includes(code) ? prev.names : [...prev.names, code]

        return {
          byName: updatedByName,
          names: updatedNames,
        }
      })

      removeFieldError(code)
    },
    [setLayeredDynamicAttributes, removeFieldError],
  )

  const setLayeredDynamicAttributeValues = useCallback(
    (selectedValues: Array<ItemAttributeModel> | undefined) => {
      if (!selectedValues) {
        setLayeredDynamicAttributes({ byName: {}, names: [] })

        return
      }

      setLayeredDynamicAttributes(reduceAttributes(selectedValues, 'ids'))
    },
    [setLayeredDynamicAttributes],
  )

  const providerValue = useMemo(
    () => ({
      selectedLayeredDynamicAttributes,
      getLayeredDynamicAttributeValuesByCode,
      setLayeredDynamicAttributeValue,
      setLayeredDynamicAttributeValues,
    }),
    [
      selectedLayeredDynamicAttributes,
      getLayeredDynamicAttributeValuesByCode,
      setLayeredDynamicAttributeValue,
      setLayeredDynamicAttributeValues,
    ],
  )

  return (
    <LayeredDynamicAttributesContext.Provider value={providerValue}>
      {children}
    </LayeredDynamicAttributesContext.Provider>
  )
}

export const useLayeredDynamicAttributesContext = () => {
  const context = useContext(LayeredDynamicAttributesContext)

  if (!context) {
    throw new Error(
      'useLayeredDynamicAttributesContext must be used within a LayeredDynamicAttributesContextProvider',
    )
  }

  return context
}
